import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { ValidationError } from "./projectRepository";

/**
 * Grouped labor settlements (Phase 9C, spec §31A).
 *
 * Crews invoice one number for many items: "four windows, two toilets and
 * that wall — $1,500". A settlement GROUPS the proposed labor lines that
 * one payment covers and points at the ONE real accounting record holding
 * the money (a manual expense or a purchase allocation).
 *
 * It deliberately stores NO amount of its own: the actual is read from the
 * accounting record, so there is no second ledger to drift, and no
 * per-item labor actual is ever invented. Comparison happens at the group.
 */

export interface MixedSettlementContext {
  lines: Array<{id:string;featureId:string|null}>;
  quoteGroups: Array<{id:string;featureIds:string[]}>;
  authorizedGroups: Array<{id:string;featureIds:string[]}>;
  sources: Array<{id:string;source:string;costType:string|null;voidedAt:string|null;amountCents:number;linkedFeatureId:string|null;linkedProposedLineId:string|null}>;
}

export interface LaborSettlement {
  coverageKind?: "labor" | "mixed-work";
  coverageSnapshot?: string | null;
  id: string;
  projectId: string;
  description: string;
  /** Exactly one of these is set (DB CHECK enforces the XOR). */
  expenseId: string | null;
  allocationId: string | null;
  notes: string;
  proposedLineIds: string[];
  createdAt: string;
  updatedAt: string;
}

interface Row {
  coverage_kind: "labor" | "mixed-work";
  coverage_snapshot: string | null;
  id: string;
  project_id: string;
  description: string;
  expense_id: string | null;
  allocation_id: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export class LaborSettlementRepository {
  constructor(private readonly db: DbExecutor) {}
  private mixedResolver: ((projectId:string)=>Promise<MixedSettlementContext>) | null = null;
  setMixedCoverageResolver(resolver:(projectId:string)=>Promise<MixedSettlementContext>):void { this.mixedResolver=resolver; }

  async listByProject(projectId: string): Promise<LaborSettlement[]> {
    const rows = await this.db.select<Row>(
      "SELECT * FROM labor_settlements WHERE project_id = ? ORDER BY created_at",
      [projectId],
    );
    if (rows.length === 0) return [];
    const links = await this.db.select<{ settlement_id: string; proposed_line_id: string }>(
      `SELECT settlement_id, proposed_line_id FROM labor_settlement_lines
        WHERE settlement_id IN (${rows.map(() => "?").join(",")})`,
      rows.map((r) => r.id),
    );
    return rows.map((r) => ({
      id: r.id,
      coverageKind:r.coverage_kind ?? "labor",coverageSnapshot:r.coverage_snapshot ?? null,
      projectId: r.project_id,
      description: r.description,
      expenseId: r.expense_id,
      allocationId: r.allocation_id,
      notes: r.notes,
      proposedLineIds: links.filter((l) => l.settlement_id === r.id).map((l) => l.proposed_line_id),
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  }

  /** proposedLineId → settlementId, for the reconciliation engine. */
  async lineIndexForProject(projectId: string): Promise<Map<string, string>> {
    const rows = await this.db.select<{ proposed_line_id: string; settlement_id: string }>(
      `SELECT l.proposed_line_id, l.settlement_id FROM labor_settlement_lines l
         JOIN labor_settlements s ON s.id = l.settlement_id
        WHERE s.project_id = ?`,
      [projectId],
    );
    return new Map(rows.map((r) => [r.proposed_line_id, r.settlement_id]));
  }

  /**
   * Group proposed labor lines under one existing accounting record.
   * The lines must be labor, belong to this project, and not already be
   * settled elsewhere — grouped labor must never double-count.
   */
  async create(
    projectId: string,
    input: {
      description: string;
      expenseId?: string | null;
      allocationId?: string | null;
      proposedLineIds: string[];
      notes?: string;
    },
  ): Promise<LaborSettlement> {
    const expenseId = input.expenseId ?? null;
    const allocationId = input.allocationId ?? null;
    if ((expenseId === null) === (allocationId === null)) {
      throw new ValidationError(["A labor settlement needs exactly one actual record (an expense or a purchase allocation)."]);
    }
    if (input.proposedLineIds.length === 0) {
      throw new ValidationError(["Choose at least one proposed labor item this payment covers."]);
    }
    const placeholders = input.proposedLineIds.map(() => "?").join(",");
    const lines = await this.db.select<{ id: string; project_id: string; cost_type: string | null; archived_at: string | null }>(
      `SELECT id, project_id, cost_type, archived_at FROM proposed_cost_lines WHERE id IN (${placeholders})`,
      input.proposedLineIds,
    );
    if (lines.length !== input.proposedLineIds.length) {
      throw new ValidationError(["One of the chosen proposed items no longer exists."]);
    }
    for (const line of lines) {
      if (line.project_id !== projectId) throw new ValidationError(["A proposed item belongs to a different project."]);
      if (line.archived_at !== null) throw new ValidationError(["An archived proposed item cannot be settled."]);
      if (line.cost_type !== "labor") {
        throw new ValidationError(["Only labor items can be grouped into a labor settlement."]);
      }
    }
    const already = await this.db.select<{ proposed_line_id: string }>(
      `SELECT proposed_line_id FROM labor_settlement_lines WHERE proposed_line_id IN (${placeholders})`,
      input.proposedLineIds,
    );
    if (already.length > 0) {
      throw new ValidationError(["One of these labor items is already part of another settlement."]);
    }

    const now = new Date().toISOString();
    const settlement: LaborSettlement = {
      id: generateId(),
      projectId,
      description: input.description.trim(),
      expenseId,
      allocationId,
      notes: input.notes ?? "",
      proposedLineIds: [...input.proposedLineIds],
      createdAt: now,
      updatedAt: now,
    };
    await this.db.transaction(async () => {
      if((await this.db.select("SELECT id FROM labor_settlements WHERE expense_id=? OR allocation_id=?",[expenseId,allocationId])).length)throw new ValidationError(["This actual record already belongs to a settlement."]);
      await this.db.execute(
        `INSERT INTO labor_settlements (id, project_id, description, expense_id, allocation_id, notes, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [settlement.id, projectId, settlement.description, expenseId, allocationId, settlement.notes, now, now],
      );
      for (const lineId of settlement.proposedLineIds) {
        await this.db.execute(
          "INSERT INTO labor_settlement_lines (proposed_line_id, settlement_id, created_at) VALUES (?, ?, ?)",
          [lineId, settlement.id, now],
        );
      }
    });
    return settlement;
  }

  /** Explicitly settles one unsplit accounting total against complete work identities. */
  async createMixed(projectId:string,input:{description:string;expenseId?:string|null;allocationId?:string|null;proposedLineIds:string[];confirmedFullSettlement:boolean;notes?:string}):Promise<LaborSettlement> {
    if(input.confirmedFullSettlement!==true)throw new ValidationError(["Confirm this total fully settles the selected work. Deposits retain their remaining estimate."]);
    if(!this.mixedResolver)throw new ValidationError(["Mixed work coverage is unavailable; record the cost without claiming settled work."]);
    return this.db.transaction(async()=>{
      const context=await this.mixedResolver!(projectId);
      const expenseId=input.expenseId??null,allocationId=input.allocationId??null;
      if((expenseId===null)===(allocationId===null))throw new ValidationError(["Choose exactly one actual expense or project allocation."]);
      const source=context.sources.find(s=>s.id===(expenseId??allocationId)&&s.source===(expenseId?"expense":"allocation"));
      if(!source||source.voidedAt||source.amountCents<=0||source.costType!==null||source.linkedFeatureId||source.linkedProposedLineId)throw new ValidationError(["Mixed coverage needs one positive, unclassified, unlinked actual record in this project."]);
      const ids=new Set(input.proposedLineIds);
      if(!ids.size||ids.size!==input.proposedLineIds.length||ids.size>500)throw new ValidationError(["Choose distinct active work lines."]);
      const selected=context.lines.filter(l=>ids.has(l.id));
      if(selected.length!==ids.size||selected.some(l=>!l.featureId))throw new ValidationError(["Choose active work items belonging to this project and current design."]);
      const features=new Set(selected.map(l=>l.featureId!));
      if(context.lines.some(l=>l.featureId&&features.has(l.featureId)&&!ids.has(l.id)))throw new ValidationError(["Select every estimated cost line for each covered work item; no per-item cost is inferred."]);
      for(const group of [...context.quoteGroups,...context.authorizedGroups]) {
        if(group.featureIds.some(id=>features.has(id))&&group.featureIds.some(id=>!features.has(id)))throw new ValidationError(["Select the whole quoted or authorized work group. A total-only invoice cannot be apportioned across partial coverage."]);
      }
      const marks=input.proposedLineIds.map(()=>"?").join(",");
      if((await this.db.select(`SELECT proposed_line_id FROM labor_settlement_lines WHERE proposed_line_id IN (${marks})`,input.proposedLineIds)).length)throw new ValidationError(["Selected work already belongs to a settlement."]);
      if((await this.db.select("SELECT id FROM labor_settlements WHERE expense_id=? OR allocation_id=?",[expenseId,allocationId])).length)throw new ValidationError(["This actual record already belongs to a settlement."]);
      const id=generateId(),now=new Date().toISOString();
      const snapshot=JSON.stringify({featureIds:[...features],proposedLineIds:[...ids],quoteGroups:context.quoteGroups.filter(q=>q.featureIds.some(f=>features.has(f)))});
      await this.db.execute("INSERT INTO labor_settlements(id,project_id,description,expense_id,allocation_id,notes,created_at,updated_at,coverage_kind,coverage_snapshot) VALUES(?,?,?,?,?,?,?,?,?,?)",[id,projectId,input.description.trim(),expenseId,allocationId,input.notes??"",now,now,"mixed-work",snapshot]);
      for(const lineId of ids)await this.db.execute("INSERT INTO labor_settlement_lines(proposed_line_id,settlement_id,created_at) VALUES(?,?,?)",[lineId,id,now]);
      return {id,projectId,description:input.description.trim(),expenseId,allocationId,notes:input.notes??"",proposedLineIds:[...ids],createdAt:now,updatedAt:now,coverageKind:"mixed-work",coverageSnapshot:snapshot};
    });
  }

  /**
   * Ungroup. The accounting record is NOT touched — the money stays where
   * it is; only the grouping is removed, so the individual labor items go
   * back to being compared on their own.
   */
  async remove(settlementId: string): Promise<void> {
    await this.db.transaction(async () => {
      await this.db.execute("DELETE FROM labor_settlement_lines WHERE settlement_id = ?", [settlementId]);
      await this.db.execute("DELETE FROM labor_settlements WHERE id = ?", [settlementId]);
    });
  }
}
